import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react'; // Assuming lucide-react for icons
import { useWallet } from '../../hooks/useWallet';
import { WalletDropdown } from './WalletDropdown';

// Reusing existing IconButton pattern from TopNavbar.tsx is ideal.
// For now, creating a similar styled button.
// TODO: Refactor to use a common IconButton if available and suitable.
const WalletButtonStyled = styled.button`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  align-items: center;
  justify-content: space-between; // To space out address and chevron
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  min-width: 150px; // Ensure enough space for address
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.paper};
    border-color: ${({ theme }) => theme.colors.primary.main};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.light}50; // Focus ring for accessibility
  }

  .address-text {
    margin-right: ${({ theme }) => theme.spacing(1)}; // Space between text and icon
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const WalletButtonContainer = styled.div`
  position: relative;
  margin-left: ${({ theme }) => theme.spacing(1)}; // Spacing from the bell icon
`;

interface WalletButtonProps {
  className?: string;
}

export const WalletButton: React.FC<WalletButtonProps> = ({ className }) => {
  const { isConnected, address, connect, status } = useWallet();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (isConnected) {
      setShowDropdown(!showDropdown);
    } else {
      connect();
    }
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let buttonContent;
  if (status === 'connecting') {
    buttonContent = <span className="address-text">Connecting...</span>;
  } else if (isConnected && address) {
    buttonContent = <span className="address-text">{formatAddress(address)}</span>;
  } else {
    buttonContent = <span className="address-text">Connect Wallet</span>;
  }

  return (
    <WalletButtonContainer className={className} ref={dropdownRef}>
      <WalletButtonStyled onClick={handleClick}>
        {buttonContent}
        {isConnected && <ChevronDown size={18} />}
      </WalletButtonStyled>
      {showDropdown && isConnected && address && (
        <WalletDropdown
          address={address}
          onClose={() => setShowDropdown(false)}
        />
      )}
    </WalletButtonContainer>
  );
}; 